import {
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ChevronDown, CircleHelp, Clock3 } from 'lucide-react';
import { useMemo } from 'react';
import {
  buildTrendMeanLabel,
  buildTrendProcessingDisclaimer,
  buildTrendViewDescription,
  getTrendFeatureInterpretation,
  TREND_LOW_PASS_DISCLAIMER,
  TREND_NORMALIZATION_DISCLAIMER,
  TREND_SECTION_HELP_TEXT,
} from '../../../shared/trend-processing';
import { TREND_FEATURE_LABELS } from '../constants';
import { useTrendChartData } from '../hooks/useTrendChartData';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
  TrendSignalKey,
} from '../types';
import { formatSegmentTabLabel } from '../utils/customSegment';
import {
  buildOverlaySessionSelectData,
  handleOverlaySessionSelection,
  TrendChartControls,
} from './TrendChartControls';
import { CustomSegmentManager } from './CustomSegmentManager';
import { TrendLineChart } from './TrendLineChart';

type TrendChartSectionProps = {
  readySessions: SpectralWorkspaceSession[];
  isLoading: boolean;
  selectedFeature: TrendSignalKey;
  onFeatureChange: (feature: TrendSignalKey) => void;
  trendNormalizationEnabled: boolean;
  onTrendNormalizationChange: (value: boolean) => void;
  trendLowPassEnabled: boolean;
  onTrendLowPassChange: (value: boolean) => void;
  showOverlaySessions: boolean;
  onShowOverlayChange: (value: boolean) => void;
  selectedOverlaySessionIds: string[];
  onOverlaySessionChange: (values: string[]) => void;
  activeViewId: ChartViewId;
  onActiveViewChange: (viewId: ChartViewId) => void;
  customSegments: CustomTimeSegment[];
  onCustomSegmentsChange: (segments: CustomTimeSegment[]) => void;
  trendStart: string | null;
  trendEnd: string | null;
  onTrendStartChange: (value: string | null) => void;
  onTrendEndChange: (value: string | null) => void;
};

type ZoomSelectsProps = {
  startOptions: Array<{ value: string; label: string }>;
  endOptions: Array<{ value: string; label: string }>;
  startValue: string | null;
  endValue: string | null;
  onStartChange: (value: string | null) => void;
  onEndChange: (value: string | null) => void;
  availableSeconds: number[];
};

function ZoomSelects({
  startOptions,
  endOptions,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  availableSeconds,
}: ZoomSelectsProps) {
  return (
    <Group gap='md' align='flex-end' wrap='wrap'>
      <Select
        label='Inizio'
        placeholder='Seleziona timestamp inizio'
        variant='filled'
        leftSection={<Clock3 size={14} />}
        rightSection={<ChevronDown size={14} />}
        data={startOptions}
        value={startValue}
        onChange={value => {
          if (!value) {
            onStartChange(null);
            return;
          }
          onStartChange(value);
          if (endValue && Number(value) >= Number(endValue)) {
            const nextSecond = availableSeconds.find(
              second => second > Number(value)
            );
            onEndChange(nextSecond != null ? nextSecond.toString() : null);
          }
        }}
        clearable
        w={220}
      />
      <Select
        label='Fine'
        placeholder='Seleziona timestamp fine'
        variant='filled'
        leftSection={<Clock3 size={14} />}
        rightSection={<ChevronDown size={14} />}
        data={endOptions}
        value={endValue}
        onChange={onEndChange}
        clearable
        w={220}
      />
    </Group>
  );
}

export function TrendChartSection({
  readySessions,
  isLoading,
  selectedFeature,
  onFeatureChange,
  trendNormalizationEnabled,
  onTrendNormalizationChange,
  trendLowPassEnabled,
  onTrendLowPassChange,
  showOverlaySessions,
  onShowOverlayChange,
  selectedOverlaySessionIds,
  onOverlaySessionChange,
  activeViewId,
  onActiveViewChange,
  customSegments,
  onCustomSegmentsChange,
  trendStart,
  trendEnd,
  onTrendStartChange,
  onTrendEndChange,
}: TrendChartSectionProps) {
  const [helpOpened, { open: openHelp, close: closeHelp }] =
    useDisclosure(false);

  const chartData = useTrendChartData({
    readySessions,
    selectedFeature,
    showOverlaySessions,
    selectedOverlaySessionIds,
    activeViewId,
    customSegments,
    trendStart,
    trendEnd,
    trendNormalizationEnabled,
    trendLowPassEnabled,
  });

  const overlaySessionSelectData = useMemo(
    () => buildOverlaySessionSelectData(readySessions),
    [readySessions]
  );

  const overlayMultiSelectValue =
    showOverlaySessions && selectedOverlaySessionIds.length === 0
      ? chartData.allOverlaySessionIds
      : selectedOverlaySessionIds;

  const signalLabel = TREND_FEATURE_LABELS[selectedFeature];
  const meanLabel = buildTrendMeanLabel(signalLabel, trendNormalizationEnabled);
  const trendViewDescription = buildTrendViewDescription(
    trendNormalizationEnabled,
    trendLowPassEnabled
  );
  const trendProcessingDisclaimer = buildTrendProcessingDisclaimer(
    trendNormalizationEnabled,
    trendLowPassEnabled
  );
  const trendFeatureInterpretation = getTrendFeatureInterpretation(
    signalLabel,
    trendNormalizationEnabled,
    trendLowPassEnabled
  );

  const activeSegment = customSegments.find(
    segment => segment.id === activeViewId
  );

  const chartTitle =
    activeViewId === 'full'
      ? 'Tratto completo'
      : activeSegment
        ? formatSegmentTabLabel(activeSegment)
        : 'Tratto custom';

  if (readySessions.length === 0) {
    return (
      <Card withBorder>
        <Text c='dimmed' size='sm'>
          Aggiungi almeno una sessione con dati HR per visualizzare il grafico.
        </Text>
      </Card>
    );
  }

  return (
    <Stack gap='md'>
      <Stack gap={4}>
        <Group justify='space-between' align='center' wrap='wrap'>
          <Title order={4}>Evoluzione segnali</Title>
          <Button
            variant='light'
            leftSection={<CircleHelp size={16} />}
            onClick={openHelp}
          >
            Come leggere il grafico
          </Button>
        </Group>
        <Text size='sm' c='dimmed'>
          {trendViewDescription}
        </Text>
      </Stack>

      <TrendChartControls
        selectedFeature={selectedFeature}
        onFeatureChange={onFeatureChange}
        normalizationEnabled={trendNormalizationEnabled}
        onNormalizationChange={onTrendNormalizationChange}
        lowPassEnabled={trendLowPassEnabled}
        onLowPassChange={onTrendLowPassChange}
        showOverlaySessions={showOverlaySessions}
        onShowOverlayChange={onShowOverlayChange}
        overlaySessionSelectData={overlaySessionSelectData}
        overlayMultiSelectValue={overlayMultiSelectValue}
        onOverlaySessionChange={values =>
          handleOverlaySessionSelection(
            values,
            chartData.allOverlaySessionIds,
            onOverlaySessionChange
          )
        }
      />

      {(trendNormalizationEnabled || trendLowPassEnabled) && (
        <Text size='sm' c='dimmed'>
          {trendProcessingDisclaimer}
        </Text>
      )}

      <CustomSegmentManager
        activeViewId={activeViewId}
        onActiveViewChange={onActiveViewChange}
        customSegments={customSegments}
        onCustomSegmentsChange={onCustomSegmentsChange}
        availableSeconds={chartData.availableTrendSeconds}
        maxAvailableSecond={chartData.maxAvailableSecond}
      />

      {isLoading && (
        <Text size='sm' c='dimmed'>
          Caricamento dati sessioni in corso…
        </Text>
      )}

      <Card withBorder>
        <Stack gap='md'>
          <Title order={5}>{chartTitle}</Title>

          {activeViewId === 'full' && (
            <ZoomSelects
              startOptions={chartData.trendStartOptions}
              endOptions={chartData.trendEndOptions}
              startValue={trendStart}
              endValue={trendEnd}
              onStartChange={onTrendStartChange}
              onEndChange={onTrendEndChange}
              availableSeconds={chartData.availableTrendSeconds}
            />
          )}

          {chartData.activeChartRows.length > 0 ? (
            <TrendLineChart
              rows={chartData.activeChartRows}
              meanLabel={meanLabel}
              overlaySessionIds={
                showOverlaySessions ? chartData.activeOverlaySessionNumbers : []
              }
              overlaySessionLabelById={chartData.overlaySessionLabelById}
            />
          ) : (
            <Text size='sm' c='dimmed'>
              Nessun dato disponibile per il segnale e l&apos;intervallo
              selezionati.
            </Text>
          )}
        </Stack>
      </Card>

      <Modal
        opened={helpOpened}
        onClose={closeHelp}
        title='Come leggere il grafico'
        centered
        size='lg'
      >
        <Stack gap='md'>
          <Text size='sm'>{TREND_SECTION_HELP_TEXT}</Text>
          <Text size='sm' fw={600}>
            {trendFeatureInterpretation.short}
          </Text>
          <Text size='sm' c='dimmed'>
            {trendFeatureInterpretation.detail}
          </Text>
          {trendNormalizationEnabled && (
            <Text size='sm' c='dimmed'>
              {TREND_NORMALIZATION_DISCLAIMER}
            </Text>
          )}
          {trendLowPassEnabled && (
            <Text size='sm' c='dimmed'>
              {TREND_LOW_PASS_DISCLAIMER}
            </Text>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
